import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Briefcase,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
  MessageSquare,
  Linkedin,
  ChevronDown,
  RefreshCw,
  ArrowLeft,
  UserCheck,
  UserX,
  PhoneCall
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import DashboardLayout from "../../components/DashboardLayout";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: "Pendiente", color: "bg-yellow-100 text-yellow-700", icon: Clock },
  contacted: { label: "Contactado", color: "bg-blue-100 text-blue-700", icon: PhoneCall },
  interviewed: { label: "Entrevistado", color: "bg-purple-100 text-purple-700", icon: MessageSquare },
  hired: { label: "Contratado", color: "bg-green-100 text-green-700", icon: UserCheck },
  rejected: { label: "Rechazado", color: "bg-red-100 text-red-700", icon: UserX }
};

const experienceLabels = {
  none: "Sin experiencia",
  less_1: "Menos de 1 año",
  "1_3": "1 a 3 años",
  "3_5": "3 a 5 años",
  more_5: "Más de 5 años"
};

const AdminJobApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedApp, setSelectedApp] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/jobs/applications`);
      setApplications(response.data.applications || []);
    } catch (error) {
      toast.error("Error al cargar solicitudes");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (appId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.put(`${API}/jobs/applications/${appId}/status?status=${newStatus}&notes=${encodeURIComponent(notes)}`);
      toast.success(`Estado actualizado a: ${statusConfig[newStatus].label}`);
      fetchApplications();
      setSelectedApp(null);
      setNotes("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar estado");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const filteredApplications = applications.filter(app => {
    if (filterStatus === "all") return true;
    return app.status === filterStatus;
  });

  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === "pending").length,
    contacted: applications.filter(a => a.status === "contacted").length,
    hired: applications.filter(a => a.status === "hired").length
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Link to="/admin" className="p-2 hover:bg-navy-100 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5 text-navy-500" />
              </Link>
              <h1 className="font-heading text-3xl font-bold text-navy-900">Solicitudes de Empleo</h1>
            </div>
            <p className="text-navy-500 ml-12">Gestiona las solicitudes de "Trabaja con Nosotros"</p>
          </div>
          <Button onClick={fetchApplications} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <p className="text-sm text-navy-500">Total Solicitudes</p>
            <p className="text-2xl font-bold text-navy-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <p className="text-sm text-navy-500">Pendientes</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
          </div>
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <p className="text-sm text-navy-500">Contactados</p>
            <p className="text-2xl font-bold text-blue-600">{stats.contacted}</p>
          </div>
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <p className="text-sm text-navy-500">Contratados</p>
            <p className="text-2xl font-bold text-green-600">{stats.hired}</p>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
          <div className="flex items-center gap-4">
            <span className="text-sm text-navy-500">Filtrar por estado:</span>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pending">Pendientes</SelectItem>
                <SelectItem value="contacted">Contactados</SelectItem>
                <SelectItem value="interviewed">Entrevistados</SelectItem>
                <SelectItem value="hired">Contratados</SelectItem>
                <SelectItem value="rejected">Rechazados</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Applications List */}
        {filteredApplications.length === 0 ? (
          <div className="bg-white rounded-xl border border-navy-100 p-12 text-center">
            <Briefcase className="w-12 h-12 text-navy-300 mx-auto mb-4" />
            <p className="text-navy-500">No hay solicitudes {filterStatus !== "all" ? `con estado "${statusConfig[filterStatus]?.label}"` : ""}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredApplications.map((app) => {
              const StatusIcon = statusConfig[app.status]?.icon || Clock;
              
              return (
                <motion.div
                  key={app.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl border border-navy-100 overflow-hidden"
                >
                  {/* Main Info */}
                  <div 
                    className="p-5 cursor-pointer hover:bg-navy-50 transition-colors"
                    onClick={() => setSelectedApp(selectedApp?.id === app.id ? null : app)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <User className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-navy-900">{app.full_name}</h3>
                          <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-navy-500">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {app.email}
                            </span>
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {app.phone}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {app.country}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusConfig[app.status]?.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig[app.status]?.label}
                        </span>
                        <ChevronDown className={`w-5 h-5 text-navy-400 transition-transform ${selectedApp?.id === app.id ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {selectedApp?.id === app.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      className="border-t border-navy-100 p-5 bg-navy-50"
                    >
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Left Column - Info */}
                        <div className="space-y-4">
                          <div>
                            <p className="text-xs font-medium text-navy-500 uppercase mb-1">Experiencia</p>
                            <p className="text-navy-900">{experienceLabels[app.experience] || app.experience}</p>
                          </div>
                          
                          {app.linkedin && (
                            <div>
                              <p className="text-xs font-medium text-navy-500 uppercase mb-1">LinkedIn</p>
                              <a 
                                href={app.linkedin.startsWith('http') ? app.linkedin : `https://${app.linkedin}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline flex items-center gap-1"
                              >
                                <Linkedin className="w-4 h-4" />
                                Ver perfil
                              </a>
                            </div>
                          )}
                          
                          <div>
                            <p className="text-xs font-medium text-navy-500 uppercase mb-1">Motivación</p>
                            <p className="text-navy-700 text-sm bg-white p-3 rounded-lg">{app.motivation}</p>
                          </div>
                          
                          <div>
                            <p className="text-xs font-medium text-navy-500 uppercase mb-1">Fecha de solicitud</p>
                            <p className="text-navy-900 flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(app.created_at).toLocaleDateString('es-ES', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>

                          {app.notes && (
                            <div>
                              <p className="text-xs font-medium text-navy-500 uppercase mb-1">Notas</p>
                              <p className="text-navy-700 text-sm bg-white p-3 rounded-lg">{app.notes}</p>
                            </div>
                          )}
                        </div>

                        {/* Right Column - Actions */}
                        <div className="space-y-4">
                          <div>
                            <p className="text-xs font-medium text-navy-500 uppercase mb-2">Actualizar Estado</p>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(statusConfig).map(([status, config]) => {
                                const Icon = config.icon;
                                return (
                                  <button
                                    key={status}
                                    onClick={() => handleUpdateStatus(app.id, status)}
                                    disabled={updatingStatus || app.status === status}
                                    className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1 transition-all
                                      ${app.status === status 
                                        ? 'ring-2 ring-offset-2 ring-emerald-500 ' + config.color
                                        : 'bg-white border border-navy-200 text-navy-600 hover:border-navy-400'
                                      }
                                      ${updatingStatus ? 'opacity-50 cursor-not-allowed' : ''}
                                    `}
                                  >
                                    <Icon className="w-4 h-4" />
                                    {config.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          <div>
                            <p className="text-xs font-medium text-navy-500 uppercase mb-2">Agregar Notas</p>
                            <Textarea
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              placeholder="Notas sobre este candidato..."
                              rows={3}
                              className="bg-white"
                            />
                          </div>

                          <div className="flex gap-2">
                            <a
                              href={`mailto:${app.email}?subject=Tu solicitud en Divídelo&body=Hola ${app.full_name},%0A%0A`}
                              className="flex-1"
                            >
                              <Button variant="outline" className="w-full">
                                <Mail className="w-4 h-4 mr-2" />
                                Enviar Email
                              </Button>
                            </a>
                            <a
                              href={`https://wa.me/${app.phone.replace(/[^0-9]/g, '')}?text=Hola ${app.full_name}, gracias por tu interés en Divídelo.`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1"
                            >
                              <Button className="w-full bg-green-600 hover:bg-green-700">
                                <Phone className="w-4 h-4 mr-2" />
                                WhatsApp
                              </Button>
                            </a>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminJobApplications;
